import { useRef, useMemo, useEffect, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF, Html } from '@react-three/drei'
import * as THREE from 'three/webgpu'
import { SkeletonUtils } from 'three-stdlib'
import animationConfig from './artemisAnimationConfig.json'

export function ArtemisOnPath({
  controlPoints,
  duration = 120,
  visible = true,
  onProgressUpdate,
  onComplete,
  trajectoryDrawComplete = false,
  onPositionUpdate,
  onScreenPositionUpdate,
  onCapsuleRef,
  onArmatureRef,
  showBrackets = false,
  bracketBlinkRef = null, // Ref object — updated imperatively in useFrame to avoid re-renders
  onSeekRef = null,
  artemisTRef = null,
  isPaused = false,
  pauseAtPercents = null,
  onMarkerReached = null,
  children
}) {
  const scale = 0.01
  const groupRef = useRef()
  const timeRef = useRef(0)
  const isCompleteRef = useRef(false)
  const animationStartedRef = useRef(false)
  const mixerRef = useRef()
  const actionsRef = useRef({})
  const scaleUpTimeRef = useRef(0)
  const hasScaledUpRef = useRef(false)
  
  // Reusable objects for useFrame to avoid creating new objects every frame
  const upVector = useRef(new THREE.Vector3(0, 1, 0))
  const quaternion = useRef(new THREE.Quaternion())
  const matrix = useRef(new THREE.Matrix4())
  const zeroVector = useRef(new THREE.Vector3(0, 0, 0))
  const euler = useRef(new THREE.Euler())
  
  // Load the Artemis model
  const gltf = useGLTF('/models/Artemis SLS alpha.glb')
  
  // Create a properly cloned scene on mount using SkeletonUtils
  const clonedScene = useMemo(() => {
    return SkeletonUtils.clone(gltf.scene)
  }, [gltf.scene])
  
  // Animation trigger — ref for the check flag (no re-render), state only increments on actual trigger (~5-6 times total)
  const shouldTriggerRef = useRef(false)
  const [triggerVersion, setTriggerVersion] = useState(0)
  const [currentAnimIndex, setCurrentAnimIndex] = useState(0)
  // Mirror of currentAnimIndex as a ref — useFrame reads this instead of the closure value
  // so it always sees the up-to-date index even in the same frame that onFinished fires
  const currentAnimIndexRef = useRef(0)
  const lastProgressRef = useRef(-1) // guard: only call onProgressUpdate when value changes
  const lastProgressExactRef = useRef(-1) // float percent for sub-point marker triggering
  const currentProgressRef = useRef(0)
  const bracketDivRef = useRef() // ref to bracket DOM element for imperative opacity updates
  const triggeredMarkersRef = useRef(new Set())

  // Track event listeners for cleanup
  const finishedListenersRef = useRef([])
  
  // Setup animation mixer and actions
  useEffect(() => {
    if (!groupRef.current || !gltf.animations.length) return
    
    // Create mixer
    mixerRef.current = new THREE.AnimationMixer(groupRef.current)
    
    // Create all actions
    gltf.animations.forEach((clip) => {
      const action = mixerRef.current.clipAction(clip)
      actionsRef.current[clip.name] = action
    })
    
    
    // Initialize: Load first animation at frame 0 and pause (matching reference code)
    if (animationConfig.animationTriggers.length > 0) {
      const firstTrigger = animationConfig.animationTriggers[0]
      const firstAction = actionsRef.current[firstTrigger.animationName]
      
      if (firstAction) {
        // Stop all animations first
        Object.values(actionsRef.current).forEach(action => action?.stop())
        
        // Set first animation to first frame and pause
        firstAction.reset()
        firstAction.setLoop(2200, 1)
        firstAction.paused = true
        firstAction.time = 0
        firstAction.play()
        
      }
    }
    
    return () => {
      // Remove all accumulated 'finished' event listeners
      finishedListenersRef.current.forEach(listener => {
        mixerRef.current?.removeEventListener('finished', listener)
      })
      finishedListenersRef.current = []
      
      if (mixerRef.current) {
        mixerRef.current.stopAllAction()
        // Properly uncache all actions and their clips
        Object.values(actionsRef.current).forEach(action => {
          if (action) {
            const clip = action.getClip()
            mixerRef.current.uncacheAction(clip)
            mixerRef.current.uncacheClip(clip)
          }
        })
        // Uncache the root object
        if (groupRef.current) {
          mixerRef.current.uncacheRoot(groupRef.current)
        }
        actionsRef.current = {}
        mixerRef.current = null
      }
    }
  }, [gltf.animations])
  
  // Cleanup cloned scene and reusable objects on unmount
  useEffect(() => {
    return () => {
      if (clonedScene) {
        clonedScene.traverse((child) => {
          if (child.geometry) child.geometry.dispose()
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(material => material.dispose())
            } else {
              child.material.dispose()
            }
          }
        })
      }
      
      // Clear reusable Three.js objects
      upVector.current = null
      quaternion.current = null
      matrix.current = null
      zeroVector.current = null
      euler.current = null
      
      // Clear group ref
      groupRef.current = null
    }
  }, [clonedScene])
  
  // Play current animation when ready (triggered by progress)
  useEffect(() => {
    if (!mixerRef.current || !actionsRef.current) return
    if (!trajectoryDrawComplete) return
    if (currentAnimIndex >= animationConfig.animationTriggers.length) return
    if (!shouldTriggerRef.current) return
    
    const trigger = animationConfig.animationTriggers[currentAnimIndex]
    const action = actionsRef.current[trigger.animationName]
    
    if (action) {
      
      // Stop all other animations
      Object.values(actionsRef.current).forEach(a => {
        if (a !== action) a?.stop()
      })
      
      // ANIMATION FRAME FIX: Prepare animation without showing reset frame
      // First, find the previous action's final state to maintain continuity
      if (currentAnimIndex > 0) {
        const prevTrigger = animationConfig.animationTriggers[currentAnimIndex - 1]
        const prevAction = actionsRef.current[prevTrigger.animationName]
        
        if (prevAction && prevAction.time > 0) {
          // Preserve the final pose from previous animation
          const finalTime = prevAction.getClip().duration
          prevAction.time = finalTime
          prevAction.paused = true
          prevAction.weight = 1
          mixerRef.current.update(0) // Apply the final pose
        }
      }
      
      // Now setup the new animation starting from the correct initial state
      action.reset()
      action.setLoop(2200, 1)
      action.clampWhenFinished = true
      
      // CRITICAL: Start paused at frame 0, then advance to frame 1 before unpausing
      action.paused = true
      action.play()
      
      // Advance one frame to avoid showing the reset pose
      const frameTime = 1 / 60 // ~60fps frame time
      action.time = frameTime
      mixerRef.current.update(0) // Apply the first real frame
      
      // Now unpause to continue normal playback
      action.paused = false
      
      // Listen for when animation finishes
      const onFinished = (e) => {
        if (e.action === action) {
          // Remove this specific listener from tracking array
          const idx = finishedListenersRef.current.indexOf(onFinished)
          if (idx > -1) finishedListenersRef.current.splice(idx, 1)
          
          mixerRef.current?.removeEventListener('finished', onFinished)
          
          // Move to next animation
          shouldTriggerRef.current = false
          currentAnimIndexRef.current = currentAnimIndex + 1 // sync ref immediately before state update
          setCurrentAnimIndex(prev => prev + 1)
        }
      }
      
      // Track listener for cleanup on unmount
      finishedListenersRef.current.push(onFinished)
      mixerRef.current.addEventListener('finished', onFinished)
      
      // Cleanup: remove listener on unmount or dependency change
      return () => {
        const idx = finishedListenersRef.current.indexOf(onFinished)
        if (idx > -1) finishedListenersRef.current.splice(idx, 1)
        mixerRef.current?.removeEventListener('finished', onFinished)
      }
    }
  }, [currentAnimIndex, trajectoryDrawComplete, triggerVersion])
  
  // Create the curve from control points
  const curve = useMemo(() => {
    if (!controlPoints || controlPoints.length < 2) return null
    
    const curvePath = new THREE.CurvePath()
    
    for (let i = 0; i < controlPoints.length - 1; i++) {
      const p0 = controlPoints[i]
      const p1 = controlPoints[i + 1]
      
      const anchor0 = new THREE.Vector3(...p0.pos)
      const anchor1 = new THREE.Vector3(...p1.pos)
      const handle0Out = new THREE.Vector3(...p0.pos).add(new THREE.Vector3(...p0.handleOut))
      const handle1In = new THREE.Vector3(...p1.pos).add(new THREE.Vector3(...p1.handleIn))
      
      const curveSegment = new THREE.CubicBezierCurve3(
        anchor0,
        handle0Out,
        handle1In,
        anchor1
      )
      
      curvePath.add(curveSegment)
    }
    
    return curvePath
  }, [controlPoints])
  
  // Cleanup curve objects on unmount or when controlPoints change
  useEffect(() => {
    return () => {
      if (curve && curve.curves) {
        // Dispose all curve segments
        curve.curves.forEach(curveSegment => {
          // Curves don't have a dispose method in Three.js, but we can clear references
          if (curveSegment.v0) curveSegment.v0 = null
          if (curveSegment.v1) curveSegment.v1 = null
          if (curveSegment.v2) curveSegment.v2 = null
          if (curveSegment.v3) curveSegment.v3 = null
        })
        curve.curves.length = 0
      }
    }
  }, [curve])
  
  // Reset function exposed to parent
  useEffect(() => {
    window.artemisReset = () => {
      timeRef.current = 0
      isCompleteRef.current = false
      animationStartedRef.current = true
      currentAnimIndexRef.current = 0
      setCurrentAnimIndex(0)
      shouldTriggerRef.current = false
      setTriggerVersion(0)
      currentProgressRef.current = 0
      lastProgressRef.current = -1
      lastProgressExactRef.current = -1
      if (artemisTRef) artemisTRef.current = null
      triggeredMarkersRef.current = new Set()

      if (mixerRef.current) {
        mixerRef.current.stopAllAction()
      }

      if (onProgressUpdate) onProgressUpdate(0)
    }
    return () => {
      delete window.artemisReset
    }
  }, [onProgressUpdate])
  
  // Restore the correct model pose for a given trajectory percent.
  // Mirrors the trigger effect exactly: stop all, then apply only the last completed
  // animation clamped at its final frame — the same way the trigger effect preserves
  // the previous action's pose before starting the next one.
  const restoreAnimationPose = (targetPercent) => {
    if (groupRef.current) {
      const t = targetPercent / 100
      if (t < 0.98) {
        groupRef.current.scale.setScalar(scale)
      } else {
        const scaleProgress = Math.max(0, (1.0 - t) / 0.02)
        groupRef.current.scale.setScalar(scale * scaleProgress)
      }
    }

    if (!mixerRef.current || !actionsRef.current) return

    // Find the index of the last trigger that has fired (triggerAtProgress <= target)
    let lastFiredIndex = -1
    for (let i = 0; i < animationConfig.animationTriggers.length; i++) {
      if (animationConfig.animationTriggers[i].triggerAtProgress <= targetPercent) {
        lastFiredIndex = i
      }
    }

    // Stop everything cleanly
    mixerRef.current.stopAllAction()

    if (lastFiredIndex < 0) {
      // Before any trigger — return to bind pose
      mixerRef.current.update(0)
      return
    }

    // Apply only the last completed action clamped at its final frame.
    // This matches exactly what the trigger effect does when it preserves the
    // previous action: paused, weight 1, time = clip.duration, then update(0).
    const lastTrigger = animationConfig.animationTriggers[lastFiredIndex]
    const lastAction = actionsRef.current[lastTrigger.animationName]

    if (lastAction) {
      lastAction.reset()
      lastAction.setLoop(2200, 1)
      lastAction.clampWhenFinished = true
      lastAction.time = lastAction.getClip().duration
      lastAction.paused = true
      lastAction.weight = 1
      lastAction.play()
    }

    mixerRef.current.update(0)
  }

  // Expose seekTo function via onSeekRef for the seek bar
  useEffect(() => {
    if (!onSeekRef) return
    onSeekRef.current = (targetPercent) => {
      const clamped = Math.max(0, Math.min(100, targetPercent))
      timeRef.current = (clamped / 100) * duration

      // Find the last trigger whose triggerAtProgress <= clamped
      let correctIndex = 0
      for (let i = 0; i < animationConfig.animationTriggers.length; i++) {
        if (animationConfig.animationTriggers[i].triggerAtProgress <= clamped) {
          correctIndex = i + 1
        }
      }
      currentAnimIndexRef.current = correctIndex
      setCurrentAnimIndex(correctIndex)

      shouldTriggerRef.current = false
      isCompleteRef.current = false
      restoreAnimationPose(clamped)
      // Reset triggered markers — re-add any already past the seek point so they don't re-fire
      triggeredMarkersRef.current = new Set(
        (pauseAtPercents ?? []).filter(p => p <= clamped)
      )
      // Force onProgressUpdate to fire on next frame
      lastProgressRef.current = clamped - 1
      lastProgressExactRef.current = clamped - 1
      // Sync sun immediately on seek
      if (artemisTRef) artemisTRef.current = clamped / 100
    }
    return () => {
      if (onSeekRef) onSeekRef.current = null
    }
  }, [duration, onProgressUpdate, onSeekRef])

  // Start animation and scale-up when trajectory drawing completes
  useEffect(() => {
    if (trajectoryDrawComplete && !animationStartedRef.current) {
      animationStartedRef.current = true
      timeRef.current = 0
      scaleUpTimeRef.current = 0
      hasScaledUpRef.current = true // DISABLED: Skip scale-up animation
      
      // DISABLED: Scale-up animation - model starts at full size
      // if (groupRef.current) {
      //   groupRef.current.scale.setScalar(0)
      // }
    }
  }, [trajectoryDrawComplete])
  
  // Find and expose Orion_Capsule for screen position tracking
  useEffect(() => {
    if (!groupRef.current) return

    // Find relevant nodes
    let orionGroup = null
    let orionCapsule = null
    const chaseCamHideNodes = []
    groupRef.current.traverse((child) => {
      if (child.name === 'orion_capsule') orionGroup = child
      if (child.name === 'Orion_Capsule') orionCapsule = child
      if (child.name === 'orion_module' || child.name === 'orion_capsule') chaseCamHideNodes.push(child)
    })

    if (onScreenPositionUpdate) {
      window.artemisGroupRef = orionCapsule || groupRef.current
    }
    if (onCapsuleRef) onCapsuleRef(orionGroup || orionCapsule || groupRef.current)
    if (onArmatureRef && chaseCamHideNodes.length) onArmatureRef(chaseCamHideNodes)
    return () => {
      if (window.artemisGroupRef) {
        delete window.artemisGroupRef
      }
    }
  }, [onScreenPositionUpdate, onCapsuleRef, clonedScene])
  
  // Animate along the path
  useFrame((state, delta) => {
    if (!groupRef.current || !curve || isCompleteRef.current || !animationStartedRef.current) return
    
    // Scale up from 0 to full size over 1 second
    // DISABLED: Model now starts at full size
    // if (!hasScaledUpRef.current) {
    //   scaleUpTimeRef.current += delta
    //   const scaleProgress = Math.min(scaleUpTimeRef.current / 1.0, 1.0) // 1 second
    //   const currentScale = scale * scaleProgress
    //   groupRef.current.scale.setScalar(currentScale)
    //   
    //   if (scaleProgress >= 1.0) {
    //     hasScaledUpRef.current = true
    //   }
    // }
    
    // Clamp delta to prevent large jumps after orientation changes or tab visibility changes
    const safeDelta = Math.min(delta, 1 / 30)

    // Update animation mixer and advance time only when not paused
    if (!isPaused) {
      if (mixerRef.current) {
        mixerRef.current.update(safeDelta)
      }
      timeRef.current += safeDelta
    }
    const t = Math.min(timeRef.current / duration, 1)

    // Write raw t into shared ref every frame for smooth sun rotation
    if (artemisTRef) artemisTRef.current = t

    // Update progress percentage
    const progressPercent = Math.floor(t * 100)
    const progressExact = t * 100
    currentProgressRef.current = progressPercent

    if (onProgressUpdate) {
      // Check if we just crossed a pause marker using exact float percent — fixes
      // sub-point markers with decimal trajectoryPercent values (e.g. 63.3) triggering late
      if (!isPaused && pauseAtPercents && onMarkerReached) {
        for (const pct of pauseAtPercents) {
          if (!triggeredMarkersRef.current.has(pct) && lastProgressExactRef.current < pct && progressExact >= pct) {
            triggeredMarkersRef.current.add(pct)
            onMarkerReached(pct)
            break
          }
        }
      }
      lastProgressExactRef.current = progressExact

      if (progressPercent !== lastProgressRef.current) {
        lastProgressRef.current = progressPercent
        onProgressUpdate(progressPercent)
      }
    }
    
    // Scale down from 98% to 100% progress (using continuous t for smooth scaling)
    if (t >= 0.98) {
      // Map 0.98-1.0 to 1.0-0.0 scale multiplier
      const scaleProgress = Math.max(0, (1.0 - t) / 0.02) // 0.02 = 2% range
      const currentScale = scale * scaleProgress
      groupRef.current.scale.setScalar(currentScale)
    }
    
    // Trigger animation when reaching trigger points
    if (currentAnimIndexRef.current < animationConfig.animationTriggers.length && !shouldTriggerRef.current) {
      const nextTrigger = animationConfig.animationTriggers[currentAnimIndexRef.current]
      if (progressPercent >= nextTrigger.triggerAtProgress) {
        shouldTriggerRef.current = true
        setTriggerVersion(v => v + 1) // fires ~5-6 times total, not every frame
      }
    }
    
    // Check if complete
    if (t >= 1 && !isCompleteRef.current) {
      isCompleteRef.current = true
      if (onComplete) onComplete()
    }
    
    // Get position on curve
    const position = curve.getPoint(t)
    groupRef.current.position.copy(position)
    
    // Get tangent for orientation
    const tangent = curve.getTangent(t)
    // Reuse objects to avoid creating new ones every frame
    matrix.current.lookAt(zeroVector.current, tangent, upVector.current)
    quaternion.current.setFromRotationMatrix(matrix.current)
    groupRef.current.quaternion.copy(quaternion.current)
    groupRef.current.rotateY(Math.PI)
    
    // Notify parent of position and rotation for chase camera
    if (onPositionUpdate) {
      euler.current.setFromQuaternion(groupRef.current.quaternion)
      onPositionUpdate(
        [position.x, position.y, position.z],
        [euler.current.x, euler.current.y, euler.current.z]
      )
    }

    // Imperatively update bracket opacity from ref — avoids React re-renders for blink animation
    if (bracketBlinkRef && bracketDivRef.current) {
      bracketDivRef.current.style.opacity = bracketBlinkRef.current
    }
  })
  
  if (!visible || !curve) return null
  
  return (
    <group ref={groupRef} scale={scale}>
      <primitive object={clonedScene} rotation={[0, 0, Math.PI / 4]} />
      {showBrackets && (
        <Html
          sprite
          zIndexRange={[0, 0]}
          style={{
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        >
          <div
            ref={bracketDivRef}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
              fontFamily: 'monospace',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#E3E3E3',
              opacity: 1,
              textShadow: '0 0 10px rgba(227, 227, 227, 0.5)'
            }}
          >
            <span>[</span>
            <span>]</span>
          </div>
        </Html>
      )}
      {children}
    </group>
  )
}

useGLTF.preload('/models/Artemis SLS alpha.glb')
